import { Ref, useCallback, useImperativeHandle } from "react";
import {
  CreatorFragmentCommonProps,
  CreatorFragmentHandle,
} from "@coworker/apprestructured/src/task-management/components/TaskCreatorAndEditorSheet/creator-fragments/CreatorFragment";
import {
  isLoading,
  LOADING,
} from "@coworker/apprestructured/src/task-management/loading";
import LoadingCreatorFragment from "@coworker/apprestructured/src/task-management/components/LoadingCreatorFragment/LoadingCreatorFragment";
import {
  getByPath,
  setByPath,
} from "@coworker/apprestructured/src/shared/helpers/object.helper";
import {
  AttachArticles,
  ItemWithQuantity,
} from "../../../AttachArticles/AttachArticles";
import {
  ItemNoAndType,
  ItemNoAndTypeWithQuantity,
} from "@coworker/apprestructured/src/task-management/types/TaskDetailTypes";
import { useAllAttachedItems } from "@coworker/apprestructured/src/task-management/services/useAllAttachedItems";
import { validateInitialDetailsFieldValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import {
  shouldShowField,
  VisibilityCondition,
} from "@coworker/apprestructured/src/task-management/helpers/visibilityCondition.helper";
import { TaskTypeNames } from "@coworker/apprestructured/src/task-management/constants/TaskTypeNames";

export type AttachArticlesCreatorFragmentConfigProps = {
  field: string;
  maxArticles: number;
  minItems?: number;
  visibility?: VisibilityCondition;
};

type AttachArticlesCreatorFragmentProps = {
  configProps: AttachArticlesCreatorFragmentConfigProps;
  commonProps: CreatorFragmentCommonProps;
  ref?: Ref<CreatorFragmentHandle>;
};

function AttachArticlesCreatorFragment({
  configProps,
  commonProps,
  ref,
}: AttachArticlesCreatorFragmentProps) {
  const { field, maxArticles, minItems = 0, visibility } = configProps;
  const { blockInput, task, onTaskChange, taskType, showValidation } =
    commonProps;

  const isCustomTask =
    !isLoading(taskType) && taskType.name === TaskTypeNames.CUSTOM_TASK;

  const rawItemNosAndType = validateInitialDetailsFieldValue<ItemNoAndType[]>(
    getByPath(task, field),
    field,
    taskType
  );

  // Normalise: add quantity=1 for items that predate the quantity field (backward compat)
  // Also handles backend returning quantity as a string (e.g. "1")
  const itemNosAndTypeWithQuantity = isLoading(rawItemNosAndType)
    ? LOADING
    : (rawItemNosAndType ?? []).map((item) => {
        const existingQty = (item as Partial<ItemNoAndTypeWithQuantity>)
          .quantity;
        const parsed =
          typeof existingQty === "string"
            ? parseInt(existingQty, 10)
            : existingQty;
        const normalized =
          typeof parsed === "number" && !isNaN(parsed) ? parsed : 1;
        const quantity = Math.max(1, normalized);
        return { ...item, quantity } as ItemNoAndTypeWithQuantity;
      });

  const { items, isFetchingItems } = useAllAttachedItems(
    isLoading(itemNosAndTypeWithQuantity) ? LOADING : itemNosAndTypeWithQuantity
  );

  const itemsWithQuantity: ItemWithQuantity[] = isLoading(
    itemNosAndTypeWithQuantity
  )
    ? []
    : items.map((item) => {
        const match = itemNosAndTypeWithQuantity.find((i) => i.no === item.no);
        return { ...item, quantity: match?.quantity ?? 1 };
      });

  const handleConfirmClick = useCallback(
    (newItemNosAndType: ItemNoAndTypeWithQuantity[]) => {
      // Strip quantity from the payload for non-custom task types
      const payload = isCustomTask
        ? newItemNosAndType
        : newItemNosAndType.map(({ no, type }) => ({ no, type }));
      onTaskChange(setByPath(field, payload));
    },
    [field, onTaskChange, isCustomTask]
  );

  const isVisible =
    !visibility || isLoading(task) || shouldShowField(task, visibility);

  const valid =
    !isVisible ||
    (!isLoading(itemNosAndTypeWithQuantity) &&
      itemNosAndTypeWithQuantity.length >= minItems);

  useImperativeHandle(ref, () => ({ valid }), [valid]);

  if (!isVisible) {
    return null;
  }

  if (isLoading(rawItemNosAndType)) {
    return <LoadingCreatorFragment />;
  }

  const showError = showValidation && !valid && minItems > 0;

  return (
    <AttachArticles
      items={itemsWithQuantity}
      setItemNosAndType={handleConfirmClick}
      disabled={blockInput || isFetchingItems}
      maxArticles={maxArticles}
      showQuantity={isCustomTask}
      showError={showError}
      minItems={minItems}
    />
  );
}

export default AttachArticlesCreatorFragment;
